import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.scss';

function Tooltip({ children, label, title, portal = false }) {
  const content = label ?? title;
  const generatedId = useId();
  const tooltipId = `custom-tooltip-${generatedId}`;
  const anchor = useRef(null), bubble = useRef(null);
  const [visible, setVisible] = useState(false), [position, setPosition] = useState({ left: 8, top: 8 });
  useLayoutEffect(() => {
    if (!portal || !visible) return undefined;
    const place = () => {
      const rect = anchor.current?.getBoundingClientRect(), box = bubble.current?.getBoundingClientRect();
      if (!rect || !box) return;
      const left = Math.max(8, Math.min(rect.left + rect.width / 2 - box.width / 2, window.innerWidth - box.width - 8));
      const top = rect.top >= box.height + 16 ? rect.top - box.height - 8 : Math.min(rect.bottom + 8, window.innerHeight - box.height - 8);
      setPosition({ left, top: Math.max(8, top) });
    };
    place(); window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [portal, visible, content]);
  const child = React.isValidElement(children)
    ? React.cloneElement(children, {
      'aria-describedby': [
        children.props['aria-describedby'],
        tooltipId,
      ].filter(Boolean).join(' ') || undefined,
    })
    : children;

  return (
    <span ref={anchor} className={`custom-tooltip${portal ? ' custom-tooltip--portal' : ''}`} data-tooltip={!portal ? content || undefined : undefined}
      onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)} onFocus={() => setVisible(true)} onBlur={() => setVisible(false)}
      onKeyDown={event => { if (event.key === 'Escape') setVisible(false); }}>
      {child}
      {portal && visible && content ? createPortal(<span ref={bubble} className="custom-tooltip__floating" style={position} id={tooltipId} role="tooltip">{content}</span>, document.body) : <span className="sr-only" id={tooltipId} role="tooltip">
        {content}
      </span>}
    </span>
  );
}

export default Tooltip;
